/* Cats n Scribbles · the bag (cart drawer and /cart). Plain script, loaded with defer after cart.js and cns.js.
   Dawn's cart.js and cart-drawer.js still own quantity changes, remove, and the section re-renders
   (cart-drawer, cart-icon-bubble, main-cart-items, main-cart-footer). This file adds the live bits:
     - one queue for every cart write and bag repaint: Dawn's quantity changes and removes (they post the line's key,
       cart.js; the row is found again by that key when their turn comes), the gift lines, the suggestion's add and
       our own re-renders. Nothing overlaps, so an older answer never paints over a newer one. Writes outside the
       queue (an add from a product form) bump a generation; a re-render that started before one is dropped and asked
       for again.
     - back from another page (a page restored from the back/forward cache, drawer open or not): one read of the bag;
       when it changed, the bag, the header count and /cart's bag repaint from the server before she can tap a line
       that has gone; every repaint tells the rest of the page (event cns:bag: the home meow tray), and so does
       every read of the bag (cns:cart after every /cart.js read: the product page's on us row)
     - dates in the delivery lines and the ship lines (CNS.rangeText / CNS.rush / CNS.door)
     - (the old "one box or two?" choice is gone: a bag that still carries the boxes attribute has it cleared once)
     - the gift note (the cart note, saved on blur, 200 characters)
     - (the final sale tick box is gone, Oct 21: the final sale line is plain text above checkout and nothing holds
       checkout back; an old bag's "final sale acknowledged" attribute is still cleared when the bag empties)
     - the suggestion: the cheapest piece that clears the gap to the next unlock (free shipping, a gift) and counts
       toward it, read from the price-sorted catalog (cns-cart-picks, cached for the session), and its add button.
       A piece in an automatic sale (cns-cart-picks d/sf) counts at its sale price while the sale's window is open.
     - Route's shipping protection: it mounts again into the fresh .route-div each repaint brings
       (data-allow-multiple-renders); the bag keeps the space while it does, and a checkout tap made before Route has
       mounted (its first mount on a fresh page, or again after a repaint) waits for Route's own checkout button, so
       protection is never skipped
     - the gift tiers (settings.cns_gifts_on): adds or removes the gift lines (property _gift = 1|2) when the qualifying
       total (the bag before order discounts, without gift lines, Route's protection, 3 for $55 pieces or gift cards:
       the gift discounts count none of them, and Shopify gives them before a creator code or kittens 10% comes off)
       crosses cns_gift_1_at / cns_gift_2_at. A gift line Shopify gives free stays, whatever that total says: its
       verdict wins (a sale's prices may count another way at checkout). Gift 2 comes in her size: the size
       she picks (cart attribute "_gift size"), else the gift line already in the bag, else the size she buys most of;
       never added without one. A gift tier Shopify gives no free unit for (the discount is missing or does not
       combine) is taken out again and rests for 10 minutes. A gift that got in twice (one unit free, one charged)
       is cut back to the free one; that tier keeps going. A gift product that came in without _gift (its page, an
       old link) goes.
     - when the bag empties, the last bag's note, old tick, boxes and gift size are cleared
     - on the way to checkout: a quiet paper state in the site's type ("off to checkout", what goes); no stamp
     - /cart on a phone: a "go to checkout" bar while the footer is off screen
     - the loyalty launcher (Smile's crown, bottom right) steps aside while the drawer is open or the checkout bar
       shows, so it never sits on a checkout button (html.cns-bag-open / html.cns-jump-on, cns-cart.css)
   It watches the bag containers and re-applies its state whenever they are re-rendered. */
(function(){
  'use strict';
  if (window.CNSCart) return;
  const CNSCart = window.CNSCart = {};

  const routesOf = () => Object.assign({cart_url:'/cart', cart_add_url:'/cart/add', cart_change_url:'/cart/change', cart_update_url:'/cart/update'}, window.routes || {});
  const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = ms => new Promise(r => setTimeout(r, ms));
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const setText = (el, tx) => { if (el && tx && el.textContent !== tx) el.textContent = tx; };
  const setStack = (el, a, b) => {
    if (el.textContent === a + b) return;
    const x = document.createElement('span'), y = document.createElement('span');
    x.textContent = a; y.textContent = b; el.replaceChildren(x, y);
  };
  const norm = s => String(s == null ? '' : s).trim().toLowerCase();
  const isGift = i => !!(i && i.properties && i.properties._gift);
  /* Route's shipping protection (added at checkout): counts toward neither free shipping nor the gifts */
  const isRouteLine = i => !!i && (i.vendor === 'Route' || /^insurance$/i.test(i.product_type || ''));
  /* a gift card: Shopify's own, or the digital "gift card" product (type Gift Card); the gifts count neither */
  const isCard = i => !!i && (i.gift_card === true || /gift card/i.test(i.product_type || ''));
  const NOTE_MAX = 200, SIZE_ATTR = '_gift size', ACK_ATTR = 'final sale acknowledged';

  /* state that must survive a re-render (null = take what the server rendered) */
  const S = {boxes:null, note:null, noteOpen:null, size:null, sizeWhere:'drawer', on:{}, failed:{}, ops:[],
    passing:false, checking:false, saves:new Set(), lastQty:0, cleared:false, msg:null, routeH:{}, routeSeen:false,
    lineErr:null, lineMax:{}, upErr:null, cartErr:null, back:0, backCart:null, held:{}, fxSent:null, fxAt:0, retryT:0};

  function post(path, body, keepalive){
    return fetch(path + '.js', {method:'POST', keepalive:!!keepalive, headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify(body)})
      .then(r => r.json().then(j => {
        if (!r.ok || (j && j.status && +j.status >= 400)) throw Object.assign(new Error((j && (j.description || j.message)) || 'cart error'), {data:j});
        return j;
      }));
  }
  /* note and attribute saves: keepalive, so a save started on blur survives leaving the page */
  function save(body){
    const p = post(routesOf().cart_update_url, body, true).catch(() => null);
    S.saves.add(p); p.finally(() => S.saves.delete(p));
    return p;
  }
  /* every bag read is shared: CNSCart.last, and a cns:cart event (cns-pdp-onus.js paints its on us row from it
     instead of reading /cart.js again) */
  const getCart = () => fetch(routesOf().cart_url + '.js', {headers:{Accept:'application/json'}, cache:'no-store'}).then(r => r.json()).then(cart => {
    if (cart && Array.isArray(cart.items)){ CNSCart.last = cart; document.dispatchEvent(new CustomEvent('cns:cart', {detail:{cart}})); }
    return cart;
  });

  /* ---------- one queue for cart writes and repaints ---------- */
  let tail = Promise.resolve(), pending = 0, gen = 0;
  function enqueue(task, ms){
    pending++;
    const run = () => Promise.race([Promise.resolve().then(task), wait(ms || 15000)]);
    const p = tail.then(run, run);
    tail = p.then(() => {}, () => {});
    tail.then(() => { pending--; });
    return p;
  }
  const idle = () => tail;
  CNSCart.idle = idle;

  /* Dawn's quantity change and remove post the line's key (cart.js), and paint by its row number n. Queue them, and
     when their turn comes find the row again by that key in the bag as it is now (a gift or the suggestion may have
     been added in front of it meanwhile). A key the server no longer has fails there and the bag repaints, so a row
     left over from another page never changes a piece she did not touch. */
  function hookDawn(){
    const CI = customElements.get('cart-items');
    if (!CI){ customElements.whenDefined('cart-items').then(hookDawn); return; }
    const P = CI.prototype;
    if (P.__cnsQueued) return;
    P.__cnsQueued = true;
    const update = P.updateQuantity, settle = P.disableLoading, regions = P.updateLiveRegions;
    P.updateQuantity = function(line, quantity, event, name, variantId){
      const self = this, tag = self.tagName.toLowerCase();
      /* the box she changed (Dawn hands its row number over 300ms later, when a repaint may have put another line at
         that number), else the row's box by its number (a remove, handed over at once) */
      const t = event && event.target;
      const input = (t instanceof HTMLInputElement && t.dataset.quantityLineKey ? t : null)
        || self.querySelector(`#Quantity-${line}`) || self.querySelector(`#Drawer-quantity-${line}`);
      const key = input && input.dataset.quantityLineKey;
      if (input && input.dataset.cnsMerged) return updateMerged(self, input, quantity);
      enqueue(() => new Promise(done => {
        const live = (self.isConnected ? self : document.querySelector(tag)) || self;
        let n = line;
        if (key){
          const now = [...live.querySelectorAll('[data-quantity-line-key]')].find(i => i.dataset.quantityLineKey === key);
          if (!now) return done();            /* that line has left the bag already */
          if (staleQty(input, now, quantity)) return done();
          n = now.dataset.index;
        }
        live.__cnsDone = done;
        /* a fresh try: Dawn's catch path writes its error here again if this one fails too */
        const errs = cartErrs(); if (errs) errs.textContent = '';
        S.cartErr = null;
        try { update.call(live, n, quantity, event, name, variantId); }
        catch (e) { live.__cnsDone = null; done(); }
      }), 20000);
    };
    /* Shopify applies what it can and still answers with an error ("Only 2 items were added to your cart due to
       availability."); on that path Dawn puts the old number back and paints nothing, so the bag would show the old
       quantity and total. Say it in the bag's words (kept through repaints for a few seconds, and the line's + stops
       at what is left), and repaint once Dawn is done. */
    P.updateLiveRegions = function(line, message){
      if (message){
        const v = voice(message);
        const input = this.querySelector(`#Quantity-${line}`) || this.querySelector(`#Drawer-quantity-${line}`);
        const key = input && input.dataset.quantityLineKey;
        if (key){
          S.lineErr = {key, msg:v.msg, until:Date.now() + 6000};
          if (v.n) S.lineMax[key] = v.n;
        }
        message = v.msg;
        this.__cnsStale = true;
      }
      return regions.call(this, line, message);
    };
    /* Dawn calls disableLoading in the finally of every updateQuantity, after it has painted */
    P.disableLoading = function(){
      try { return settle.apply(this, arguments); }
      finally {
        const d = this.__cnsDone; this.__cnsDone = null;
        /* the catch path (the request failed): Dawn wrote its sentence and painted nothing either. The drawer's slot
           sits inside what the repaint replaces, so the sentence is kept through it for a few seconds (hydrate), then
           goes from the drawer. /cart's slot sits outside the repaint and keeps it until her next try, as before. */
        const errs = cartErrs();
        if (errs && errs.textContent.trim()){
          const msg = voice(errs.textContent).msg, e = {msg, until:Date.now() + 6000};
          setText(errs, msg); S.cartErr = e; this.__cnsStale = true;
          setTimeout(() => {
            if (S.cartErr !== e) return;
            S.cartErr = null;
            const el = cartErrs(); if (el && el.closest('cart-drawer') && el.textContent === msg) el.textContent = '';
          }, 6000);
        }
        const stale = this.__cnsStale; this.__cnsStale = false;
        if (d) d();
        if (stale) refresh();
      }
    };
  }
  /* a box on a bag back from the back/forward cache (pageshow marks them): her number was worked out from the
     quantity its row showed. When the repaint after Back brought another quantity, the number is dropped, and so is
     every other tap from that box (the row shows the real one now; a remove still goes). The same when the Back
     check read another quantity for the row but could not repaint it (a 429 on the render: the row still shows the
     old one, and the bag is asked to repaint again). Once a tap from it passes (its row was right, or the Back check
     had nothing else to say), the box counts as fresh: a second + queued behind the first still goes. */
  function staleQty(input, now, quantity){
    if (!input.__cnsOld) return false;
    if (now !== input && quantity > 0 && now.getAttribute('value') !== input.getAttribute('value')) return true;
    if (now === input && quantity > 0 && S.backCart && backQty(input) !== Number(input.getAttribute('value'))){
      input.value = input.getAttribute('value'); refresh(); return true;
    }
    input.__cnsOld = false;
    return false;
  }
  /* the row's quantity in the bag the Back check read (a split piece's row sums its lines); NaN when one has gone */
  const backQty = input => [input.dataset.quantityLineKey, ...String(input.dataset.cnsMerged || '').split(',')].filter(Boolean)
    .reduce((s, k) => { const i = (S.backCart.items || []).find(x => x.key === k); return i ? s + i.quantity : NaN; }, 0);
  /* One piece split in two lines by the gift discount shows as one row (cns-cart-body); its quantity box carries the
     other lines' keys. A change or a remove sets the first line to the new quantity and the others to 0 in one
     /cart/update (Shopify splits it again as it needs), then the bag repaints. Dawn's own path would change one line
     by its number and leave the other part. */
  function updateMerged(host, input, quantity){
    const tag = host.tagName.toLowerCase(), was = [input.dataset.quantityLineKey, ...String(input.dataset.cnsMerged).split(',')].filter(Boolean);
    const spin = input.closest('.cns-line') && input.closest('.cns-line').querySelector('.loading__spinner');
    if (spin) spin.classList.remove('hidden');
    enqueue(async () => {
      /* the row as it is now (a repaint may have moved the split): the box that still holds one of these keys */
      const live = (host.isConnected ? host : document.querySelector(tag)) || host;
      const now = [...live.querySelectorAll('input[data-quantity-line-key]')].find(i => [i.dataset.quantityLineKey, ...String(i.dataset.cnsMerged || '').split(',')].some(k => was.includes(k)));
      if (now && staleQty(input, now, quantity)){ if (spin) spin.classList.add('hidden'); return; }
      const keys = now ? [now.dataset.quantityLineKey, ...String(now.dataset.cnsMerged || '').split(',')].filter(Boolean) : was;
      const updates = {}; keys.forEach((k, i) => { updates[k] = i ? 0 : quantity; });
      try {
        const c = await post(routesOf().cart_update_url, {updates});
        /* as Dawn does after a change (its bag elements skip this source; ours repaint below) */
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, {source:'cart-items', cartData:c});
      } catch (e){
        const v = voice((e && e.message) || 'error');
        S.lineErr = {key:keys[0], msg:v.msg, until:Date.now() + 6000};
        if (v.n) S.lineMax[keys[0]] = v.n;
      }
      await paint();
    }, 20000);
  }
  const cartErrs = () => document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
  /* her next write (a change, an add, a size): Dawn's failure sentence is over, so it never sits under the bag that
     write paints as if that one had failed */
  function clearCartErr(){
    S.cartErr = null;
    const el = cartErrs(); if (el && el.textContent) el.textContent = '';
  }
  /* Shopify's and Dawn's quantity messages in the bag's words; n = how many the bag could keep, when it says */
  function voice(m){
    const t = String(m || '').trim();
    const k = t.match(/only (\d+) items? (?:was|were) added/i) || t.match(/only add (\d+)/i) || t.match(/^all (\d+) /i);
    if (k){ const n = Number(k[1]); return {n, msg: n === 1 ? "that's the last one." : `only ${n} left, so your bag has\u00a0${n}.`}; }
    /* "The maximum quantity of this item is already in your cart." (an add of a piece the bag already holds all of).
       It gives no count, and 2 or more left can all be in the bag, so the words give none either (it read 'the last
       one'); product-form.js says the same */
    if (/maximum quantity.*already in your cart/i.test(t)) return {n:0, msg:"all that's left is already in your\u00a0bag."};
    /* "try again." stays together (a narrow card note left "again." alone); the rest wraps, as a 320px drawer line
       has no room for "that didn't go through." on one line */
    return {n:0, msg: t ? "that didn't go through. try\u00a0again." : ''};
  }
  /* after a repaint: the line's message (written back through repaints for 6 seconds, then it stays until the next one
     re-renders the row), found by its key or its piece (a gift coming in re-keys the line), and its + stops at what is
     left (Dawn's own rule reads max) */
  function applyLineErr(bag){
    bag.querySelectorAll('input[data-quantity-line-key]').forEach(inp => {
      const key = inp.dataset.quantityLineKey, row = inp.closest('.cns-line');
      const max = S.lineMax[key];
      if (max){
        if (inp.max !== String(max)) inp.max = String(max);
        const plus = row && row.querySelector('.quantity__button[name="plus"]');
        if (plus) plus.classList.toggle('disabled', Number(inp.value) >= max);
      }
      const e = S.lineErr, mine = e && (e.key === key || (!!e.vid && Number(inp.dataset.quantityVariantId) === e.vid)
        || String(inp.dataset.cnsMerged || '').split(',').includes(e.key));
      if (mine && Date.now() < e.until) setText(row && row.querySelector('.cart-item__error-text'), e.msg);
    });
  }
  let listening = false;
  function listenDawn(){
    if (listening || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    listening = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, ev => {
      gen++;
      /* a write that went through (an add from a product form, a change): before its own render paints */
      clearCartErr();
      const c = ev && ev.cartData;
      if (ev && ev.source === 'cart-items' && c && c.item_count === 0) clearLeftovers(c);
      /* a change made in the drawer while it is open over /cart: Dawn repaints only the drawer and the page's
         <cart-items> skips that source, so the page would keep the old bag (and its form the old quantities).
         Repaint the page's bag too, after Dawn's own update in the queue. */
      if (ev && ev.source === 'cart-items' && document.getElementById('main-cart-items') && document.querySelector('cart-drawer.active')) enqueue(() => paint('page'));
    });
  }

  /* ---------- dates: the same ranges as the product page and the order confirmation email ---------- */
  function fillDates(scope){
    const C = window.CNS; if (!scope || !C || !C.rangeText) return;
    scope.querySelectorAll('[data-cns-date]').forEach(el => {
      const what = el.dataset.cnsDate, kind = el.dataset.kind === 'mto' ? 'mto' : 'ready';
      if (what === 'today') return setText(el, C.fmt(C.today()));
      if (what === 'rush') return setText(el, 'by ' + C.fmt(C.rush()));
      /* the timeline stacks a range on two lines and breaks before "to", like the cards: "oct 26" / "to oct 29" */
      const r = what === 'range' ? C.range(kind) : what === 'door' ? C.door(kind) : null;
      if (!r) return;
      if (el.hasAttribute('data-stack')) setStack(el, C.fmt(r[0]), 'to ' + C.fmt(r[1]));
      else setText(el, C.fmt(r[0]) + ' to ' + C.fmt(r[1]));
    });
    /* (the lines say only their kind: the range is the bag's, said once at the top; cns-cart-line) */
  }
  const nbDates = r => window.CNS.fmt(r[0]).replace(' ', '\u00a0') + ' to ' + window.CNS.fmt(r[1]).replace(' ', '\u00a0');

  /* ---------- the two places a bag lives ---------- */
  function hosts(){
    return [document.querySelector('cart-drawer'), document.querySelector('cart-items.cns-cart-page'), document.getElementById('main-cart-footer')].filter(Boolean);
  }
  const bags = () => [...document.querySelectorAll('[data-cns-bag]')];
  const feet = () => [...document.querySelectorAll('[data-cns-foot]')];
  const whereOf = el => (el && el.closest && el.closest('cart-drawer')) ? 'drawer' : 'page';

  /* ---------- re-apply everything after a render ---------- */
  function hydrate(){
    /* /cart has its own wallet buttons (main-cart-footer): the drawer behind the page drops its copy (cns-cart-foot,
       theme setting cns_drawer_wallets), at load before Shopify starts them and after every repaint, so the page holds
       one set of Shopify's wallet ids and the subscription consent shows under the buttons she sees */
    if (document.getElementById('main-cart-footer')) document.querySelectorAll('cart-drawer .cns-express').forEach(n => n.remove());
    const bs = bags();
    if (bs.length) S.cleared = false;
    bs.forEach(bag => {
      const ctx = bag.dataset.cnsBag;
      fillDates(bag);
      const host = bag.closest('cart-drawer, cart-items');
      if (host) host.querySelectorAll('[data-cns-bag-count]').forEach(el => setText(el, bag.dataset.count));
      applyBoxes(bag);
      /* "one box or two?" is gone (the whole order ships together): clear a choice an older bag saved */
      if (bag.dataset.boxesLeft && !S.boxesCleared){ S.boxesCleared = true; save({attributes:{boxes:''}}); }
      applyNote(bag);
      applySize(bag);
      applyLineErr(bag);
      const gifts = readGifts(bag).g;
      /* a code switched the gifts off (byCode): in the box says so once (cns-cart-body data-cns-code-note) and
         promises no gift at all, a tier still to come included (the same code switches off every gift discount),
         nor says what doesn't count toward one (data-cns-gift-why) */
      const code = byCode();
      bag.querySelectorAll('[data-cns-gift]').forEach(li => {
        const n = li.dataset.cnsGift, k = ctx + ':' + n, on = li.dataset.on === '1';
        /* a tier whose gift Shopify charged for rests: promise nothing for it */
        const rest = code || (on && charged(n));
        if (li.hidden !== rest) li.hidden = rest;
        /* reached, but its gift line is not in the bag yet (on its way, waiting for a size, or a failed add waiting
           for its retry): the row stays quiet until it is */
        const todo = !on || !(gifts[n] && gifts[n].q > 0);
        if (li.classList.contains('cns-todo') !== todo) li.classList.toggle('cns-todo', todo);
        if (on && S.on[k] === false && !reduce()) li.classList.add('cns-pop');
        S.on[k] = on;
      });
      bag.querySelectorAll('[data-cns-gift-sizes]').forEach(li => { const rest = code || charged(2); if (li.hidden !== rest) li.hidden = rest; });
      bag.querySelectorAll('[data-cns-code-note]').forEach(li => { if (li.hidden === code) li.hidden = !code; });
      bag.querySelectorAll('[data-cns-gift-why]').forEach(el => { if (el.hidden !== code) el.hidden = code; });
    });
    /* gift 2 resting: the rail stops asking for a size */
    if (charged(2)) document.querySelectorAll('[data-cns-size-jump]').forEach(a => {
      const span = a.parentElement, trk = span && span.closest('.cns-trk');
      if (span && span.dataset.cnsRest) setText(span, span.dataset.cnsRest); else if (trk) trk.hidden = true;
    });
    /* and the rail names free shipping only (cns-cart-rail data-cns-rail-ship, not data-cns-ship: that one is cns.js's
       ship line, which would repaint it as "ready to ship"; none: the rail goes), without its bar */
    if (byCode()) document.querySelectorAll('.cns-trk > span[data-cns-rail-ship]').forEach(span => {
      const trk = span.closest('.cns-trk'), bar = trk.querySelector('.cns-rail');
      if (span.dataset.cnsRailShip) setText(span, span.dataset.cnsRailShip); else trk.hidden = true;
      if (bar && !bar.hidden) bar.hidden = true;
    });
    fillUps();
    keepRoute();
    edges();
    /* Dawn's failure sentence, for a few seconds through the repaint it asked for (P.disableLoading) */
    if (S.cartErr && Date.now() < S.cartErr.until) setText(cartErrs(), S.cartErr.msg);
    setTimeout(sayMsg, 150);
    watchJump();
    watchDrawerJump();
    /* some paths re-render the bag body and its footer separately; a mismatch means one of them is stale */
    const byCtx = {};
    [...bs, ...feet()].forEach(el => { const c = el.dataset.cnsBag || el.dataset.cnsFoot; (byCtx[c] = byCtx[c] || new Set()).add(el.dataset.sig); });
    if (Object.values(byCtx).some(s => s.size > 1)) refreshSoon();
    /* an empty bag that still carries the last bag's note or tick */
    if (!bs.length && document.querySelector('[data-cns-leftover]')) clearLeftovers(null);
  }

  function applyBoxes(bag){
    const radios = [...bag.querySelectorAll('[data-cns-boxes]')];
    if (!radios.length) return;
    const v = S.boxes || (radios.find(r => r.checked) || {}).value || 'one';
    radios.forEach(r => { const c = r.value === v; if (r.checked !== c) r.checked = c; });
    bag.querySelectorAll('[data-cns-box]').forEach(b => { const h = b.dataset.cnsBox !== v; if (b.hidden !== h) b.hidden = h; });
  }

  function applyNote(bag){
    const ta = bag.querySelector('[data-cns-note]'), btn = bag.querySelector('[data-cns-note-toggle]');
    if (!ta || !btn) return;
    if (S.note !== null && ta.value !== S.note && document.activeElement !== ta) ta.value = S.note;
    /* a note longer than 200 (only possible from outside the bag) is cut to 200 and saved once */
    if (ta.value.length > NOTE_MAX && document.activeElement !== ta){
      ta.value = ta.value.slice(0, NOTE_MAX);
      if (S.note !== ta.value){ S.note = ta.value; save({note:S.note}); }
    }
    setText(bag.querySelector('[data-cns-note-left]'), String(Math.max(0, NOTE_MAX - ta.value.length)));
    if (S.noteOpen !== null){
      const f = document.getElementById(btn.getAttribute('aria-controls'));
      if (f && f.hidden === S.noteOpen) f.hidden = !S.noteOpen;
      const ex = String(S.noteOpen); if (btn.getAttribute('aria-expanded') !== ex) btn.setAttribute('aria-expanded', ex);
    }
    setText(btn, ta.value.trim() ? 'edit gift note' : 'add a gift note');
  }

  function applySize(bag){
    if (S.size === null) return;
    bag.querySelectorAll('[data-cns-gift-size]').forEach(r => { const c = norm(r.value) === norm(S.size); if (r.checked !== c) r.checked = c; });
  }

  /* Route's shipping protection clones the checkout button (name "button-route-N") and re-clicks the hidden original */
  const isRoute = el => !!el && /^button-route/i.test(el.getAttribute('name') || '');
  const routeBtn = foot => foot ? [...foot.querySelectorAll('button[name^="button-route"]')].find(b => b.isConnected) || null : null;
  /* Route mounts again into the fresh .route-div a repaint brings (about a second later). Until it does, the empty
     copy keeps the toggle's last height, so the footer never jumps; after 6s without Route it lets go. */
  const routeT = {};
  /* Route first drops in an empty widget and fills it a moment later: count it mounted once it has height */
  const routeInner = d => d.childElementCount ? Math.max(0, ...[...d.children].map(c => c.getBoundingClientRect().height)) : 0;
  /* Route's toggle height, remembered for the session per bag and layout; before any, its usual height (50px on a
     phone, 51px on a wide /cart, measured Oct 25) */
  const routeKey = ctx => 'cns-routeH-' + ctx + (innerWidth >= 990 ? '-w' : '-n');
  const ROUTE_H = 50;
  function keepRoute(){
    feet().forEach(foot => {
      const ctx = foot.dataset.cnsFoot, d = foot.querySelector('.route-div');
      if (!d) return;
      if (d.childElementCount) S.routeSeen = true;
      if (routeInner(d) > 0){
        if (d.style.minHeight){ d.style.minHeight = ''; d.dataset.cnsHeld = 'done'; }
        const h = d.offsetHeight;
        if (h > 0 && S.routeH[ctx] !== h){ S.routeH[ctx] = h; try { sessionStorage.setItem(routeKey(ctx), String(h)); } catch (e) { /* memory only */ } }
        return;
      }
      /* a fresh page, before Route's first mount here: hold the height it had last time this session (or its usual
         height), so checkout does not drop when the toggle lands (it came 2.5s after the bag on /cart, measured Oct 25) */
      let h = S.routeH[ctx];
      if (!h && !d.dataset.cnsHeld && routeOn()){
        try { h = Number(sessionStorage.getItem(routeKey(ctx))) || 0; } catch (e) { h = 0; }
        if (!h) h = ROUTE_H;
      }
      if (!h || d.dataset.cnsHeld) return;
      d.dataset.cnsHeld = '1'; d.style.minHeight = h + 'px';
      clearTimeout(routeT[ctx]);
      routeT[ctx] = setTimeout(() => { if (d.isConnected && !routeInner(d)){ d.style.minHeight = ''; d.dataset.cnsHeld = 'gone'; } }, 6000);
    });
  }
  /* Route's app is on for this shop: its widget is defined, or its loader is on the page (a script from route.com, or
     the URL in Shopify's app loader, which adds the script on window load). The .route-div alone proves nothing: the
     theme always prints it. */
  let routeKnown = false;
  function routeOn(){
    if (routeKnown) return true;
    if (window.customElements && customElements.get('route-protect-widget')) return (routeKnown = true);
    routeKnown = [...document.scripts].some(sc => sc.src ? /\.route\.com\//i.test(sc.src) : /widget\.route\.com/i.test(sc.textContent || ''));
    return routeKnown;
  }
  /* Route is ready for a tap once its checkout button copy exists AND its toggle has drawn (the copy comes first; a
     tap on it before the toggle is there goes on without protection, measured Oct 22) */
  const routeReady = foot => {
    const b = routeBtn(foot), d = foot && foot.querySelector('.route-div');
    return b && d && routeInner(d) > 0 ? b : null;
  };
  /* she tapped checkout before Route was ready: on a fresh page before its first mount (its toggle draws 2 to 6s after
     the bag, later on a phone), or while it mounts again after a repaint. Wait for it (up to 6s for a first mount,
     2.5s for a re-mount; "off to checkout" shows meanwhile) and hand the tap to Route's button, so the pre-ticked protection is
     added as usual. Resolves the Route button, or null to go on without it (Route is off or never came). */
  function routeHandoff(foot){
    if (!foot || !foot.querySelector('.route-div')) return Promise.resolve(null);
    const now = routeReady(foot);
    if (now) return Promise.resolve(now);
    const first = !S.routeSeen;
    if (first && !routeOn()) return Promise.resolve(null);
    const limit = first ? 6000 : 2500;
    return new Promise(done => {
      const t0 = Date.now();
      let readyAt = 0;
      const tick = () => {
        const f = document.getElementById(foot.id) || foot, b = routeReady(f);
        /* once ready, a short beat for Route to read its toggle */
        if (b && !readyAt) readyAt = Date.now();
        if (b && Date.now() - readyAt >= 300) return done(b);
        if (Date.now() - t0 > limit) return done(routeReady(f));
        setTimeout(tick, 100);
      };
      tick();
    });
  }

  /* the drawer's list scrolls under a sticky footer: a paper fade at its lower edge while there is more below */
  function edge(el){
    const more = el.scrollHeight - el.clientHeight - el.scrollTop > 4 && el.scrollHeight > el.clientHeight + 4;
    const d = el.closest('cart-drawer');
    if (d && d.classList.contains('cns-more') !== more) d.classList.toggle('cns-more', more);
  }
  function edges(){ document.querySelectorAll('cart-drawer cart-drawer-items').forEach(edge); }
  document.addEventListener('scroll', e => { if (e.target instanceof Element && e.target.matches('cart-drawer cart-drawer-items')) edge(e.target); }, true);
  addEventListener('resize', () => edges(), {passive:true});

  /* ---------- controls ---------- */
  function onBoxes(r){
    S.boxes = r.value;
    bags().forEach(applyBoxes);
    save({attributes:{boxes:S.boxes}});
  }
  function onNoteInput(ta){
    S.note = ta.value.slice(0, NOTE_MAX);
    bags().forEach(bag => { const o = bag.querySelector('[data-cns-note]'); if (o && o !== ta && o.value !== ta.value) o.value = ta.value; applyNote(bag); });
  }
  function onNoteSave(ta){
    S.note = ta.value.slice(0, NOTE_MAX);
    save({note:S.note});
  }
  function toggleNote(btn){
    S.noteOpen = btn.getAttribute('aria-expanded') !== 'true';
    bags().forEach(applyNote);
    if (S.noteOpen){ const f = document.getElementById(btn.getAttribute('aria-controls')); const ta = f && f.querySelector('textarea'); if (ta) ta.focus(); }
  }
  function addUpsell(btn){
    if (btn.getAttribute('aria-disabled') === 'true') return;
    /* aria-disabled, not disabled: the button keeps focus while it works */
    btn.setAttribute('aria-disabled', 'true'); btn.setAttribute('aria-busy', 'true');
    const id = Number(btn.dataset.cnsUpsell), p = btn.dataset.cnsUpsellP || '', where = whereOf(btn), old = !!btn.__cnsOld;
    enqueue(async () => {
      clearCartErr();
      /* a card left over from before a repaint (back from another page) can offer a piece the bag holds by now (a
         fresh card never does: choose), or an offer the repainted bag no longer makes (the bag was emptied, or
         reached what the card was for, on another page). Nothing goes in. When the bag holds the piece, her line
         says so (its message slot is an alert) and gets the focus; otherwise the bag's status line says why. */
      const slot = document.querySelector(`[data-cns-bag="${where}"] [data-cns-up]`);
      const has = !!p && !!slot && String(slot.dataset.in || '').split(',').includes(p);
      if (has || (old && !btn.isConnected && ![...document.querySelectorAll(`[data-cns-upsell="${id}"]`)].some(b => whereOf(b) === where))){
        if (btn.isConnected){ btn.removeAttribute('aria-disabled'); btn.removeAttribute('aria-busy'); }
        const inp = document.querySelector(`[data-cns-bag="${where}"] [data-quantity-variant-id="${id}"]`);
        if (inp){ S.lineErr = {key:inp.dataset.quantityLineKey, vid:id, msg:'already in your bag.', until:Date.now() + 6000}; bags().forEach(applyLineErr); }
        else announce(where, has ? 'already in your bag.' : 'your bag changed. nothing was added.');
        if (inp || has) focusLine(where, id);
        else { const d = where === 'drawer' && document.querySelector('cart-drawer'); if (d && d.classList.contains('active')) trapIn(d, null); }
        return;
      }
      try {
        await post(routesOf().cart_add_url, {items:[{id, quantity:1}]});
        await paint();
        focusLine(where, id);
        const foot = document.querySelector(`[data-cns-foot="${where}"]`);
        announce(where, 'added to your bag.' + (foot && foot.dataset.total ? ' subtotal ' + foot.dataset.total + '.' : ''));
      } catch (e) {
        /* it did not go in: a card left over from another page offering a piece the bag already holds ("The maximum
           quantity of this item is already in your cart."), a 429, offline. Read the bag again and say so inside it (a
           toast sits under an open drawer): on the piece's own line when the bag holds it, else under the card's add */
        const raw = (e && e.message) || 'error', v = voice(raw), until = Date.now() + 6000;
        S.upErr = {id, msg:v.msg, until};
        const cart = await paint();
        if (btn.isConnected){ btn.removeAttribute('aria-disabled'); btn.removeAttribute('aria-busy'); }
        const line = cart && (cart.items || []).find(i => i.variant_id === id && !isGift(i));
        if (line){
          if (v.n || /maximum quantity/i.test(raw)){ S.lineErr = {key:line.key, vid:id, msg:v.msg, until}; bags().forEach(applyLineErr); }
          focusLine(where, id);
          return;
        }
        fillUps();
        if (!btn.isConnected){
          const again = [...document.querySelectorAll(`[data-cns-upsell="${id}"]`)].find(b => whereOf(b) === where);
          const d = where === 'drawer' && document.querySelector('cart-drawer');
          if (d){ if (d.classList.contains('active')) trapIn(d, again || null); }
          else if (again) again.focus();
        }
        announce(where, v.msg);
      }
    });
  }

  /* ---------- the suggestion: the cheapest piece that unlocks the next thing ---------- */
  /* Candidates come from the whole catalog sorted by price, low to high (cns-cart-picks, printed by main-cart-footer
     on /collections/all), 250 pieces a page, kept for the session per currency, country and theme. Pages 1..n hold
     every piece priced under the last page's "to", so the first candidate at or above the gap is the cheapest one
     as long as it sits under "to"; otherwise the next page is read (at most PICK_PAGES). */
  const PICK_TTL = 20 * 60 * 1000, PICK_PAGES = 3;
  let picks = null, picksFor = '', pickLoad = null, pickFail = 0;
  const pickKey = () => {
    const sh = window.Shopify || {};
    return ['cns-picks-v2', (sh.currency && sh.currency.active) || '', sh.country || '', (sh.theme && sh.theme.id) || ''].join(':');
  };
  function picksNow(){
    const k = pickKey();
    if (picksFor !== k){ picks = null; picksFor = k; }
    if (picks && Date.now() - picks.at < PICK_TTL) return picks;
    picks = null;
    try {
      const j = JSON.parse(sessionStorage.getItem(k) || 'null');
      if (j && Array.isArray(j.pages) && j.pages.length && Date.now() - j.at < PICK_TTL) picks = j;
    } catch (e) { picks = null; }
    return picks;
  }
  function pickUrl(n){
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return `${root}collections/all?section_id=main-cart-footer&sort_by=price-ascending&filter.v.availability=1&page=${n}`;
  }
  /* reads the next page; resolves true when it added one */
  function morePicks(){
    if (pickLoad) return pickLoad;
    if (Date.now() - pickFail < 60000) return Promise.resolve(false);
    const k = pickKey(), cur = picksNow() || {at:Date.now(), pages:[]}, last = cur.pages[cur.pages.length - 1];
    if ((last && !last.to) || cur.pages.length >= PICK_PAGES) return Promise.resolve(false);
    const n = cur.pages.length + 1;
    pickLoad = fetch(pickUrl(n), {credentials:'same-origin'})
      .then(r => { if (!r.ok) throw new Error('picks ' + r.status); return r.text(); })
      .then(html => {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cns-picks]');
        const page = el && JSON.parse(el.textContent);
        if (!page || !Array.isArray(page.c)) throw new Error('picks: no list');
        if (pickKey() !== k || cur.pages.length !== n - 1) return false;
        cur.pages.push({to:Number(page.to) || 0, sale:page.sale || null, c:page.c});
        picks = cur; picksFor = k;
        try { sessionStorage.setItem(k, JSON.stringify(cur)); } catch (e) { /* private mode: memory only */ }
        return true;
      })
      .catch(() => { pickFail = Date.now(); return false; })
      .finally(() => { pickLoad = null; });
    return pickLoad;
  }
  /* warm the list ahead of the bag (once a session): only once the bag has pieces (an idle moment), or the moment
     she adds her first piece from a product form. An empty bag never asks for it. */
  function warmPicks(){
    if (picksNow() || pickLoad) return;
    morePicks();
  }
  function warmSoon(){
    if (!bags().length) return;
    idleRun(warmPicks);
  }
  document.addEventListener('submit', e => {
    const f = e.target;
    if (f instanceof HTMLFormElement && /\/cart\/add/.test(f.getAttribute('action') || '')) warmPicks();
  }, true);

  /* what the bag still unlocks (from the slot cns-cart-body prints), or null when nothing is within $60 */
  const giftCore = s => String(s || '').split(',')[0].trim().replace(/\s+on us$/, '');
  function readUp(slot){
    const [qual, gqual, free, g1, g2, cap] = String(slot.dataset.cnsUp || '').split('|').map(Number);
    const tiers = [];
    /* a code switched the gifts off: the card never offers a piece for a gift */
    const code = byCode();
    if (free > 0 && qual < free) tiers.push({k:'s', need:free - qual});
    if (!code && g1 > 0 && gqual < g1 && !charged(1)) tiers.push({k:'1', need:g1 - gqual, name:giftCore(slot.dataset.g1n)});
    if (!code && g2 > 0 && gqual < g2 && !charged(2)) tiers.push({k:'2', need:g2 - gqual, name:giftCore(slot.dataset.g2n)});
    if (!tiers.length) return null;
    const gap = Math.min(...tiers.map(t => t.need));
    if (!(gap > 0 && gap <= cap)) return null;
    const inBag = new Set(String(slot.dataset.in || '').split(',').filter(Boolean).map(Number));
    /* what the rail right above the card already names (cns-cart-body: s = free shipping, 1, 2 = the gifts) */
    const near = String(slot.dataset.near || '').split('').sort().join('');
    return {gap, tiers, kind:slot.dataset.kind || 'any', inBag, near};
  }
  /* an automatic sale (cns-cart-picks: d = percent off, sale = its window): while it is open, a sale piece counts
     at its sale price, the price the bag will really add (rounded down, so a promise never falls a cent short) */
  function saleOf(P){
    const s = (P.pages[0] && P.pages[0].sale) || {}, now = Date.now() / 1000;
    const on = s.t > 0 && now >= s.f && now <= s.t;
    const off = on ? Math.max(0, ...P.pages.map(p => Math.max(0, ...p.c.map(c => Number(c.d) || 0)))) : 0;
    return {on, off};
  }
  const effOf = (c, sale) => (sale.on && c.d) ? c.pr - Math.ceil(c.pr * Number(c.d) / 100) : c.pr;
  /* the cheapest piece at or above the gap and not in the bag; ready to ship first in a ready bag (so the bag's
     ship range stays put), made to order only when nothing ready clears it. null = none, undefined = read more. */
  function choose(w){
    const P = picksNow();
    if (!P) return undefined;
    const to = P.pages[P.pages.length - 1].to, all = !to || P.pages.length >= PICK_PAGES, sale = saleOf(P);
    const pool = [];
    /* a gap that only a gift closes: a piece in the sale never counts toward a gift (cns-cart-calc 20), so it is no
       offer for one */
    const giftOnly = !w.tiers.some(t => t.k === 's' && t.need === w.gap);
    P.pages.forEach(p => p.c.forEach(c => {
      const e = effOf(c, sale);
      if (e >= w.gap && !w.inBag.has(c.p) && !(giftOnly && sale.on && c.d)) pool.push(Object.assign({}, c, {e, sale: sale.on && !!c.d}));
    }));
    /* cheapest; at the same price, a one size piece (it adds in one tap) */
    const best = list => list.reduce((b, c) => (!b || c.e < b.e || (c.e === b.e && !b.v && c.v)) ? c : b, null);
    /* a later page only holds pieces at or above "to", which a sale can bring down by its percent */
    const floor = to ? to - Math.ceil(to * sale.off / 100) : 0;
    const sure = c => !!c && (!to || c.e < floor);
    const pick = w.kind === 'ready' ? best(pool.filter(c => c.k === 'ready')) : best(pool);
    if (sure(pick)) return pick;
    if (!all) return undefined;
    return pick || best(pool);
  }
  /* The rail right above the card already says what is next and how far ("add $1 for free shipping in the us and
     the bow clip."), so a card that clears exactly that only names the piece: "the sweet steps socks will do it."
     A piece that clears more says the rest: "..., and a panty's on us too." When the rail names something else (a
     gift tier resting), the card says it all: "add the hair clips and a panty's on us." */
  function upWords(w, c){
    /* a sale piece clears free shipping only, never a gift */
    const hit = w.tiers.filter(t => c.e >= t.need && !(c.sale && t.k !== 's'));
    if (!hit.length) return '';
    const piece = (/^the\s/.test(c.t) ? '' : 'the ') + `<a href="${esc(c.u)}" class="cns-ul">${esc(c.t)}</a>`;
    const names = ts => ts.filter(t => t.k !== 's' && t.name).map(t => esc(t.name));
    const and = a => a.length > 1 ? a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1] : (a[0] || '');
    const said = w.tiers.filter(t => t.need === w.gap).map(t => t.k).sort().join('');
    if (w.near && w.near === said){
      const more = hit.filter(t => t.need > w.gap), ship = more.some(t => t.k === 's'), gifts = names(more);
      let tail = '';
      if (ship) tail = `, and shipping's free too${gifts.length ? ', plus ' + and(gifts) : ''}`;
      else if (gifts.length) tail = `, and ${and(gifts)}${gifts.length > 1 ? ' are' : '\'s'} on us too`;
      return `${piece} will do it${tail}.`;
    }
    const ship = hit.some(t => t.k === 's'), gifts = names(hit);
    if (!ship && !gifts.length) return '';
    if (ship) return `add ${piece} and shipping's free${gifts.length ? ', plus ' + and(gifts) : ''}.`;
    return `add ${piece} and ${and(gifts)}${gifts.length > 1 ? ' are' : '\'s'} on us.`;
  }
  /* what her failed add of this piece said (addUpsell), for 6 seconds of repaints (the card then keeps it until the bag
     next repaints); '' otherwise */
  const upErrOf = c => { const e = S.upErr; return e && c && c.v && Number(c.v) === e.id && Date.now() < e.until ? e.msg : ''; };
  function upCard(w, c){
    const words = upWords(w, c);
    if (!words) return '';
    const u = esc(c.u), t = esc(c.t), h = Math.ceil(40 / (Number(c.r) || 0.8));
    const img = c.i ? `<img src="${esc(c.i)}" alt="" loading="lazy" width="40" height="${h}">` : '';
    const act = c.v
      ? `<button type="button" class="cns-up__add cns-t" data-cns-upsell="${Number(c.v)}" data-cns-upsell-p="${Number(c.p)}" aria-label="add ${t}">add</button>`
      : `<a href="${u}" class="cns-up__add cns-t" aria-label="choose a size, ${t}">choose a size</a>`;
    /* in the sale: the catalog price struck, the sale price the bag will show */
    const price = c.sale && c.sf
      ? `<span class="cns-up__pr"><s class="cns-mute"><span class="cns-sr">was </span>${esc(c.pf)}</s> <span>${esc(c.sf)}</span></span>`
      : `<span class="cns-up__pr cns-mute">${esc(c.pf)}</span>`;
    /* a made to order piece for a ready bag (only when nothing ready clears the gap): say what it does to the date */
    const C = window.CNS, late = w.kind === 'ready' && c.k === 'mto' && C && C.range
      ? `<span class="cns-up__note cns-d cns-mute">made to order. your bag would ship ${esc(nbDates(C.range('mto')))}.</span>` : '';
    /* her add of this piece just failed (addUpsell): the reason under its button */
    const said = upErrOf(c), err = said ? `<span class="cns-up__note cns-d">${esc(said)}</span>` : '';
    /* the words get the card's full width; the price and the button share the row under them */
    return `<div class="cns-up cns-t"><a href="${u}" class="cns-up__img" tabindex="-1" aria-hidden="true">${img}</a>`
      + `<div class="cns-up__dd"><span class="cns-up__w">${words}</span>${late}`
      + `<span class="cns-up__row">${price}${act}</span>${err}</div></div>`;
  }
  /* while the list loads on /cart, the card keeps its place (no jump when it lands) */
  const UP_WAIT = '<div class="cns-up cns-up--wait" aria-hidden="true"><span class="cns-up__img"><i></i></span><span class="cns-up__dd"><i></i><i></i></span></div>';
  function clearWaits(){
    document.querySelectorAll('[data-cns-up]').forEach(slot => {
      if (slot.dataset.shown !== 'wait') return;
      slot.dataset.shown = ''; slot.innerHTML = ''; slot.hidden = true;
    });
  }
  const idleRun = f => ('requestIdleCallback' in window) ? requestIdleCallback(f, {timeout:4000}) : setTimeout(f, 1500);
  let upIdle = false;
  /* now = read the list right away; otherwise a bag nobody is looking at (a closed drawer) waits for an idle moment */
  function fillUps(now){
    const slots = [...document.querySelectorAll('[data-cns-up]')];
    if (!slots.length) return;
    let more = false;
    /* the list could not be read a moment ago: no card for now (no retry loop), the next try comes after a minute */
    const failing = Date.now() - pickFail < 60000;
    slots.forEach(slot => {
      const w = readUp(slot);
      let c = w ? choose(w) : null;
      if (c === undefined && failing) c = null;
      if (c === undefined){
        more = true; slot.dataset.late = '1';
        if (!slot.closest('cart-drawer') && slot.dataset.shown !== 'wait'){ slot.dataset.shown = 'wait'; slot.innerHTML = UP_WAIT; slot.hidden = false; }
        return;
      }
      const html = c ? upCard(w, c) : '';
      const key = html ? c.p + ':' + (c.sale ? 's' : '') + w.tiers.filter(t => c.e >= t.need).map(t => t.k).join('') + (upErrOf(c) ? ':e' : '') : '-';
      if (slot.dataset.shown === key) return;
      /* her focus is on the card (its add, after a failed add): it stays on the new card's add, never drops to the page */
      const had = slot.contains(document.activeElement);
      slot.dataset.shown = key;
      slot.innerHTML = html;
      if (slot.hidden !== !html) slot.hidden = !html;
      if (had){
        const b = slot.querySelector('.cns-up__add');
        if (b) b.focus({preventScroll:true});
        else { const d = slot.closest('cart-drawer'); if (d && d.classList.contains('active')) trapIn(d, null); }
      }
      /* it arrived after the bag (the list was still loading): ease it in */
      if (html && slot.dataset.late === '1' && !reduce()) slot.classList.add('cns-up-in');
    });
    if (!more || pickLoad) return;
    if (now || slots.some(x => !x.closest('cart-drawer') || x.closest('cart-drawer.active'))){
      morePicks().then(added => { if (added) fillUps(true); else clearWaits(); });
    } else if (!upIdle){
      upIdle = true;
      idleRun(() => { upIdle = false; fillUps(true); });
    }
  }

  /* gift 2 size chips. Arrow keys walk a radio group, so wait for her to settle before writing. */
  let sizeQueued = false;
  function onGiftSize(r){
    S.size = r.value; S.sizeWhere = whereOf(r);
    bags().forEach(applySize);
    if (sizeQueued) return;
    sizeQueued = true;
    enqueue(async () => {
      await wait(250);
      sizeQueued = false;
      clearCartErr();
      const size = S.size, where = S.sizeWhere;
      document.querySelectorAll('[data-cns-sizes]').forEach(f => f.setAttribute('aria-busy', 'true'));
      await post(routesOf().cart_update_url, {attributes:{[SIZE_ATTR]: size}}).catch(() => null);
      delete S.failed[2]; dropRest(2);
      const cart = await syncNow(true);
      if (S.size === size) S.size = null;
      document.querySelectorAll('[data-cns-sizes]').forEach(f => f.removeAttribute('aria-busy'));
      /* in the box only when Shopify really gives it (a charged gift line comes straight out again) */
      const has = cart && (cart.items || []).some(i => isGift(i) && String(i.properties._gift) === '2' && i.final_line_price === 0);
      announce(where, has ? `size ${size}, it is in the box.` : 'could not add it. try again.');
    });
  }

  /* a short status for screen readers, in the bag the control lives in; re-applied after re-renders */
  function announce(where, msg){
    S.msg = {where, msg, until:Date.now() + 3000};
    const el = document.getElementById('CnsBagLive-' + where); if (el) el.textContent = '';
    setTimeout(sayMsg, 120);
  }
  function sayMsg(){
    const m = S.msg; if (!m || Date.now() > m.until) return;
    const el = document.getElementById('CnsBagLive-' + m.where);
    if (el && el.textContent !== m.msg) el.textContent = m.msg;
  }

  /* ---------- focus across re-renders ---------- */
  function trapIn(drawer, el){
    const box = drawer.classList.contains('is-empty') ? drawer.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
    const to = el || drawer.querySelector('.drawer__inner');
    if (box && typeof trapFocus === 'function') trapFocus(box, to);
    else if (to) to.focus();
  }
  function focusHeading(){
    const h = document.querySelector('cart-items:not(.is-empty) .cns-cart-titlebar .cns-cart-title') || document.querySelector('.cart__warnings .cns-cart-title');
    if (!h) return;
    h.setAttribute('tabindex', '-1'); h.focus();
  }
  /* call before a repaint; the function it returns puts focus back on the same control (by id) or somewhere sane.
     A link (a line's name: focusLine puts her there after an add, and a gift coming in repaints the bag) is found
     again by where it goes. Back from another page (S.back: a click on a desktop left focus on the link she went
     by), the page does not move: no scroll to it, no heading when it has gone. */
  function keepFocus(){
    const a = document.activeElement;
    if (!a || a === document.body) return () => {};
    const host = hosts().find(h => h.contains(a));
    if (!host) return () => {};
    /* a quantity box is found again by its line key (its row number can move); anything else by id */
    const id = a.id, name = /^updates\[/.test(a.name || '') ? a.name : '', inDrawer = host.matches('cart-drawer');
    const back = S.back > 0, href = a.matches('a[href]') ? a.getAttribute('href') : '';
    /* a line's + / - or remove (no id, no updates[] name): found again in the row that still holds one of its line
       keys (a split piece's row can come back led by its other line). A change Shopify refuses puts focus back on it
       (cart.js), then this repaint replaces the row: it went to the bag's panel, or /cart's heading (Oct 31 review) */
    const keysOf = i => [i.dataset.quantityLineKey, ...String(i.dataset.cnsMerged || '').split(',')].filter(Boolean);
    const ctl = a.closest('quantity-input, cart-remove-button'), row = ctl && a.closest('.cns-line');
    const box = row && row.querySelector('input[data-quantity-line-key]'), keys = box ? keysOf(box) : [];
    const ctlSel = !keys.length ? '' : a.matches('.quantity__button[name]') ? `.quantity__button[name="${a.name}"]`
      : ctl.matches('cart-remove-button') ? 'cart-remove-button > *' : '';
    const ctlEl = () => {
      const i = [...host.querySelectorAll('input[data-quantity-line-key]')].find(x => keysOf(x).some(k => keys.includes(k)));
      const r = i && i.closest('.cns-line');
      return (r && r.querySelector(ctlSel)) || null;
    };
    return () => {
      /* a control the repaint kept but hid is lost too (/cart's title bar heading, hidden once the bag empties) */
      if (a.isConnected && a.getClientRects().length) return;
      const el = ctlSel ? ctlEl()
        : name ? [...host.querySelectorAll('input[name^="updates["]')].find(i => i.name === name) || null
        : (href && [...host.querySelectorAll('a[href]')].find(x => x.getAttribute('href') === href && x.className === a.className))
          || (id ? document.getElementById(id) : null);
      if (inDrawer){
        const d = document.querySelector('cart-drawer');
        if (d && d.classList.contains('active')) trapIn(d, el);
      } else if (back){ if (el) el.focus({preventScroll:true}); }
      else if (el) el.focus();
      else focusHeading();
    };
  }
  function focusLine(where, variantId){
    const host = where === 'drawer' ? document.querySelector('cart-drawer') : document.querySelector('cart-items');
    if (!host) return;
    const inp = host.querySelector(`[data-quantity-variant-id="${variantId}"]`);
    const row = inp && inp.closest('.cns-line'), link = row && row.querySelector('.cart-item__name');
    if (where === 'drawer'){ if (host.classList.contains('active')) trapIn(host, link); }
    else if (link) link.focus();
    else focusHeading();
  }

  /* ---------- re-render the bag sections ---------- */
  const staleTimes = [];
  /* refreshes asked for because a render looked stale; capped so a stale response can never loop */
  function staleOk(){ const now = Date.now(); while (staleTimes.length && now - staleTimes[0] > 10000) staleTimes.shift(); if (staleTimes.length >= 3) return false; staleTimes.push(now); return true; }
  let soonT = 0;
  function refreshSoon(){ if (soonT || !staleOk()) return; soonT = setTimeout(() => { soonT = 0; refresh(); }, 120); }
  /* the section rendering API on /cart (so the cart template's sections resolve). No Accept: application/json here,
     or Shopify answers /cart with the cart object instead of the sections. */
  function fetchSections(ids, bust){
    const q = 'sections=' + ids.join(',') + (bust ? '&_=' + Date.now() : '');
    return fetch(`${routesOf().cart_url}?${q}`, {cache:'no-store'}).then(res => res.json());
  }
  const sigOf = html => ((html || '').match(/data-sig="([^"]+)"/) || [])[1] || null;
  /* queued callers share one repaint that has not started yet */
  let queuedRefresh = null;
  function refresh(){
    if (queuedRefresh) return queuedRefresh;
    const p = queuedRefresh = enqueue(() => { queuedRefresh = null; return paint(); });
    return p;
  }
  /* A section that comes back exactly as this file last painted it, and untouched since, is left as it is: a no-op
     repaint would only make Route mount again and read the cart five more times. "Untouched" = the same top-level
     nodes, bag and footer (every Dawn repaint replaces at least one of them). */
  const marks = el => [...el.children, el.querySelector('[data-cns-bag]'), el.querySelector('[data-cns-foot]')];
  function put(dst, html){
    const was = dst.__cnsPainted, now = marks(dst);
    if (was && was.html === html && was.marks.length === now.length && was.marks.every((m, i) => m === now[i])) return;
    dst.innerHTML = html;
    dst.__cnsPainted = {html, marks:marks(dst)};
  }
  let redoT = 0;
  /* runs inside the queue only; only = 'page' repaints /cart's bag and leaves the drawer and the header be; quiet =
     the repaint after gift lines moved and nothing else did (no cns:bag: nothing outside the bag shows a gift) */
  async function paint(only, quiet){
    const g0 = gen;
    const drawer = only === 'page' ? null : document.querySelector('cart-drawer');
    const mi = document.getElementById('main-cart-items'), mf = document.getElementById('main-cart-footer');
    const ids = [];
    if (drawer) ids.push('cart-drawer');
    if (only !== 'page' && document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    if (mi && mi.dataset.id) ids.push(mi.dataset.id);
    if (mf && mf.dataset.id) ids.push(mf.dataset.id);
    if (!ids.length) return null;
    try {
      let [sections, cart] = await Promise.all([fetchSections(ids), getCart()]);
      /* a render that disagrees with the cart is a cached or racing response: ask once more, uncached */
      const want = cart.item_count + ':' + cart.total_price;
      const got = Object.values(sections || {}).map(sigOf).find(Boolean);
      const missing = ids.some(id => !(sections && typeof sections[id] === 'string'));
      if (missing || (cart.item_count && got && got !== want)) sections = await fetchSections(ids, true).catch(() => sections);
      /* a write outside the queue (an add from a product form) landed while this was in flight: its own render is
         newer than this one. Drop this paint and look once more. */
      if (gen !== g0){
        if (!redoT) redoT = setTimeout(() => { redoT = 0; refresh(); }, 200);
        return cart;
      }
      const sec = sections || {}, parse = h => new DOMParser().parseFromString(h, 'text/html');
      const empty = !cart.item_count;
      const restore = keepFocus();
      if (drawer && sec['cart-drawer']){
        const src = parse(sec['cart-drawer']).querySelector('.drawer__inner'), dst = drawer.querySelector('.drawer__inner');
        if (src && dst) put(dst, src.innerHTML);
        drawer.classList.toggle('is-empty', empty);
      }
      if (sec['cart-icon-bubble']){
        const src = parse(sec['cart-icon-bubble']).querySelector('.shopify-section'), dst = document.getElementById('cart-icon-bubble');
        if (src && dst) put(dst, src.innerHTML);
      }
      [[mi, 'cart-items'], [mf, null]].forEach(([el, tag]) => {
        if (!el || !sec[el.dataset.id]) return;
        const src = parse(sec[el.dataset.id]).querySelector('.js-contents'), dst = el.querySelector('.js-contents');
        if (src && dst) put(dst, src.innerHTML);
        (tag ? el.closest(tag) : el)?.classList.toggle('is-empty', empty);
      });
      restore();
      S.backCart = null;
      if (empty) clearLeftovers(cart);
      /* what else on the page shows the bag follows this repaint (cns-home.js: the meow tray; Dawn's cartUpdate is
         not published here, its own bag elements would render a second time) */
      if (!quiet) document.dispatchEvent(new CustomEvent('cns:bag', {detail:{cart}}));
      return cart;
    } catch (e) { return null; }
  }

  /* ---------- the last bag's note, tick, boxes and gift size go when the bag empties ---------- */
  function clearLeftovers(cart){
    if (S.cleared) return;
    const a = (cart && cart.attributes) || null;
    const dirty = !cart || cart.note || (a && (a[ACK_ATTR] || a.boxes || a[SIZE_ATTR]));
    if (!dirty) return;
    S.cleared = true;
    S.note = null; S.boxes = null; S.size = null; S.noteOpen = null;
    save({note:'', attributes:{[ACK_ATTR]:'', boxes:'', [SIZE_ATTR]:''}});
  }

  /* ---------- gifts: the theme adds the gift line, the buy X get Y discount makes it free ---------- */
  const failedAt = n => S.failed[n] || null;
  const isFailed = n => { const f = failedAt(n); return !!f && f.until > Date.now(); };
  const charged = n => { const f = failedAt(n); return !!f && f.charged && f.until > Date.now(); };
  const markFailed = (n, ms, why, k) => { S.failed[n] = {until:Date.now() + ms, charged:why === 'charged', k}; };
  /* a tier rests because a code switched the gift discounts off (its charged rest was taken while a code was on) */
  const byCode = () => [1, 2].some(n => charged(n) && !!(failedAt(n) || {}).k);
  /* a charged rest taken while a code is on the bag outlives the page, for as long as the same codes are on: a code
     that combines with nothing turns the gift discounts off on every page, so a fresh page would add the gift and
     take it out again each time. Other codes (or none) end it. Kept for the session (cns-gift-rest: codes, tier → until). */
  const REST_KEY = 'cns-gift-rest';
  const codesOf = cart => (cart.discount_codes || []).filter(d => d && d.applicable).map(d => norm(d.code)).sort().join(',');
  function readRest(){ try { const r = JSON.parse(sessionStorage.getItem(REST_KEY) || 'null'); return r && r.t ? r : null; } catch (e) { return null; } }
  function writeRest(r){
    try { if (r && Object.keys(r.t).length) sessionStorage.setItem(REST_KEY, JSON.stringify(r)); else sessionStorage.removeItem(REST_KEY); }
    catch (e) { /* memory only */ }
  }
  function keepRest(k, n, until){ const r = readRest(); const x = r && r.k === k ? r : {k, t:{}}; x.t[n] = until; writeRest(x); }
  function dropRest(n){ const r = readRest(); if (r && r.t[n]){ delete r.t[n]; writeRest(r); } }
  /* at the start of each plan: a charged rest taken under other codes is over; the session's rest for these codes
     comes back */
  function restsFor(k){
    [1, 2].forEach(n => { const f = S.failed[n]; if (f && f.charged && f.k !== k) delete S.failed[n]; });
    const r = readRest();
    if (!r) return;
    if (r.k !== k){ writeRest(null); return; }
    const now = Date.now();
    [1, 2].forEach(n => {
      const u = Number(r.t[n]) || 0, f = S.failed[n];
      if (u > now && !(f && f.until >= u)) S.failed[n] = {until:u, charged:true, k};
    });
  }

  function readGifts(bag){
    /* variant|threshold|qty in the bag|variant in the bag|charged|free (Shopify gives one unit free: cns-cart-body) */
    const p = s => { const a = String(s || '').split('|'); return {v:Number(a[0]) || 0, at:Number(a[1]) || 0, q:Number(a[2]) || 0, cur:Number(a[3]) || 0, ch:a[4] === '1', fr:a[5] === '1'}; };
    let vars = null;
    try { vars = JSON.parse(bag.dataset.g2Vars || 'null'); } catch (e) { vars = null; }
    const g2 = Object.assign(p(bag.dataset.g2), {vars:Array.isArray(vars) && vars.length ? vars : null});
    const trio = new Set(String(bag.dataset.trio || '').split(',').filter(Boolean).map(Number));
    return {on:bag.dataset.gifts === 'on', qual:Number(bag.dataset.qual) || 0, trio, g:{1:p(bag.dataset.g1), 2:g2},
      giftP:new Set(String(bag.dataset.giftP || '').split(',').filter(Boolean).map(Number)), stray:bag.dataset.stray === '1'};
  }
  /* cheap check from what the server rendered; the real decision is made on a fresh /cart.js */
  function needsGiftSync(){
    return bags().some(bag => {
      const c = readGifts(bag);
      if (c.on && c.stray) return true;                    /* a gift product without _gift goes (plan) */
      return [1, 2].some(n => {
        const g = c.g[n];
        if (g.ch) return true;                              /* a gift line that costs money */
        if (!c.on) return g.q > 0;                          /* gifts are off: a leftover gift line goes */
        /* reached, or Shopify gives it free in the bag right now (its verdict wins, plan) */
        const want = !!g.v && ((c.qual > 0 && c.qual >= g.at) || g.fr);
        if (!want) return g.q > 0;
        return !isFailed(n) && (g.q !== 1 || g.cur !== g.v);
      });
    });
  }
  /* a line's panty size: its option named "size", else a set's bottom ("kitty courtesan g-string (size)",
     "bottom size"), never a top, a cup or a shoe. cns-cart-calc runs the same rule in Liquid. */
  const BOTTOM = /thong|string|brief|panty|kini|short|bottom|hipster/, NOT_BOTTOM = /cup size|shoe|top|bra|cami|skirt|tube|accessory/;
  const sizeOpt = i => {
    const os = i.options_with_values || [];
    return os.find(x => norm(x.name) === 'size') || os.find(x => { const k = norm(x.name); return k.includes('size') && BOTTOM.test(k) && !NOT_BOTTOM.test(k); }) || null;
  };
  /* gift 2's size: the one she picked, else the gift line already in the bag, else the size she buys most of */
  function pickSize(cart, vars, lines){
    const ok = vars.filter(v => v.a);
    const attr = cart.attributes && cart.attributes[SIZE_ATTR];
    if (attr){ const v = ok.find(x => norm(x.s) === norm(attr)); if (v) return v.id; }
    const cur = lines.map(l => vars.find(x => x.id === l.variant_id)).find(Boolean);
    if (cur) return cur.id;
    let best = null, most = 0;
    ok.forEach(v => {
      let n = 0;
      (cart.items || []).forEach(i => {
        if (isGift(i)) return;
        const o = sizeOpt(i);
        if (o && norm(o.value) === norm(v.s)) n += i.quantity;
      });
      if (n > most){ most = n; best = v; }
    });
    return best ? best.id : 0;
  }
  /* how many units of a gift line Shopify gives free. The gift discount frees one unit, so a gift that got in twice
     (two page loads adding it at once, or a quantity of 2) comes back from Shopify split in two lines with the same
     variant and properties and different keys: one free, one charged (measured Oct 22). A line that kept both units
     shows the free one as a discount of one unit's price. */
  const freeUnits = l => {
    const q = Number(l.quantity) || 0, orig = Number(l.original_line_price) || 0, fin = Number(l.final_line_price) || 0;
    if (!q) return 0;
    if (fin <= 0) return q;
    return orig > 0 ? Math.floor((orig - fin) / (orig / q) + 0.01) : 0;
  };
  /* a piece that keeps an automatic sale of its own (a line level automatic discount, black cat weekend's 20%):
     Shopify leaves it out of the gifts' minimum while it keeps that discount (cns-cart-calc 20). A code never counts. */
  const onSale = i => (i.line_level_discount_allocations || []).some(a => a && a.amount > 0 && a.discount_application && a.discount_application.type !== 'discount_code');
  function plan(cart, c){
    const r = routesOf(), ops = [];
    const giftLines = (cart.items || []).filter(isGift);
    /* a gift product added outside the bag's own gift lines (its page, an old link: no _gift). The buy X get Y frees any
       unit of the gift products, so a stray clip can take the real gift's free slot and leave her panty charged. While
       the gifts are on it goes, and counts toward nothing meanwhile */
    const strays = c.on ? (cart.items || []).filter(i => !isGift(i) && c.giftP.has(i.product_id)) : [];
    strays.forEach(l => ops.push(() => post(r.cart_change_url, {id:l.key, quantity:0}).catch(() => retrySoon(5000))));
    /* the gift discounts buy from the gift-qualifiers collection, one-time purchases only: no 3 for $55 pieces, no gift
       cards, no Route, no kitty club subscription, no piece that keeps a % sale of its own. Leave those out, as
       cns-cart-calc does (keep cns-pdp-onus.js on the product page in step). */
    const outLines = (cart.items || []).filter(i => !isGift(i) && (isCard(i) || isRouteLine(i) || !!i.selling_plan_allocation || (c.trio && c.trio.has(i.product_id)) || onSale(i)));
    /* the lines' prices before order discounts (items_subtotal_price: a creator code or kittens 10% comes off after the
       gifts are given, so its cut never takes one away; cns-cart-calc gift_qual). Free shipping is not decided here. */
    /* (each line once: a stray the gift discount frees also reads as a sale of its own) */
    const qual = cart.items_subtotal_price - [...new Set([...giftLines, ...outLines, ...strays])].reduce((s, i) => s + i.final_line_price, 0);
    const k = codesOf(cart);
    restsFor(k);
    [1, 2].forEach(n => {
      const g = c.g[n], lines = giftLines.filter(i => String(i.properties._gift) === String(n));
      /* Shopify's own verdict, where the bag shows it: a gift it gives free stays, whatever the sum above says (a
         sale's prices may count another way in its buy X get Y minimum), and a rest is over */
      const given = lines.some(l => freeUnits(l) > 0);
      if (given){ delete S.failed[n]; dropRest(n); }
      /* her new size's line did not go in after its free one went out (a size swap; a 429, offline): the tier stays
         hers through the retry, as Shopify's verdict on the old line may have been all that kept it (set below) */
      const held = !lines.length && S.held[n] > Date.now();
      if (lines.length) delete S.held[n];
      const qualifies = c.on && ((qual > 0 && qual >= g.at) || given || held);
      /* never charge for a "gift": when not one unit of this tier is free, the discount is missing or does not
         combine. Take it out and rest this tier. A tier with a free unit and an extra (charged) one works: the extra
         goes below and the free one stays, with no rest (cns-cart-body marks "charged" by the same rule). */
      /* Only while the bag qualifies: under the threshold Shopify stops discounting the gift line by design, and that
         line just goes (below), with no rest, so the gift comes back the moment the bag is over again. */
      /* Not while a stray is in: it may hold the free unit this line needs. It goes first, and the repaint after it
         syncs again with Shopify's verdict on the bag without it. */
      if (qualifies && lines.length && !given && !strays.length){ markFailed(n, 600000, 'charged', k); if (k) keepRest(k, n, S.failed[n].until); }
      let target = 0;
      if (qualifies && !isFailed(n)) target = (n === 2 && g.vars) ? pickSize(cart, g.vars, lines) : g.v;
      /* keep one line of the right variant: the free single first, then one carrying a free unit (cut to one
         below), then any (the free unit sits on another size, and moves to this one once that line is out) */
      const mine = target ? lines.filter(l => l.variant_id === target) : [];
      const keep = mine.find(l => l.quantity === 1 && freeUnits(l) === 1) || mine.find(l => freeUnits(l) > 0) || mine[0] || null;
      if (given && target && !keep) S.held[n] = Date.now() + 30000;
      lines.forEach(l => { if (l !== keep) ops.push(() => post(r.cart_change_url, {id:l.key, quantity:0}).catch(() => retrySoon(5000))); });
      if (target && !keep) ops.push(() => post(r.cart_add_url, {items:[{id:target, quantity:1, properties:{_gift:String(n)}}]}).catch(() => { markFailed(n, 20000, 'error'); retrySoon(20500); }));
      if (keep && keep.quantity !== 1) ops.push(() => post(r.cart_change_url, {id:keep.key, quantity:1}).catch(() => null));
    });
    return ops;
  }
  /* a gift write that failed (a 429, offline) is tried again on its own once the tier's rest is over */
  function retrySoon(ms){
    clearTimeout(S.retryT);
    S.retryT = setTimeout(() => { if (bags().length) syncGifts(); }, ms);
  }
  /* a gift tier waiting out a failed add (not a charged one): checkout gives it one more try first */
  const retrying = () => [1, 2].some(n => isFailed(n) && !charged(n));
  function isStale(cart, list){
    const els = list || [...bags(), ...feet()], sig = cart.item_count + ':' + cart.total_price;
    if (!cart.item_count) return els.length > 0;
    if (!els.length) return hosts().length > 0;
    return els.some(el => el.dataset.sig !== sig);
  }
  /* runs inside the queue only; user = she asked (a size chip), so the loop cap does not apply */
  async function syncNow(user){
    const bag = bags()[0];
    try {
      const cart = await getCart();
      const now = Date.now(); S.ops = S.ops.filter(t => now - t < 30000);
      const ops = bag && !fxPending() ? plan(cart, readGifts(bag)) : [];
      if (ops.length && (user || S.ops.length < 12)){
        /* the bag on screen was the server's: only gift lines move, and the repaint is a quiet one */
        const quiet = !isStale(cart);
        for (const op of ops){ S.ops.push(Date.now()); await op(); }
        return (await paint(null, quiet)) || cart;
      }
      /* a rest this plan brought back (the session's, for these codes) with nothing to write: the rows were drawn
         before it was known, so they still promise the gift. Hide them now (only the hidden attribute moves). */
      if (bag && !ops.length && [1, 2].some(charged)) hydrate();
      if (isStale(cart) && (user || staleOk())) return (await paint()) || cart;
      if (!cart.item_count) clearLeftovers(cart);
      return cart;
    } catch (e) { return null; }       /* quiet: the bag still works without gifts */
  }
  let syncQueued = null;
  function syncGifts(){
    if (syncQueued) return syncQueued;
    const p = syncQueued = enqueue(() => { syncQueued = null; return syncNow(false); });
    return p;
  }
  CNSCart.syncGifts = syncGifts;
  CNSCart.refresh = refresh;

  /* ---------- on the way to checkout: a quiet paper state, type only ---------- */
  /* "off to checkout" and what goes ("2 pieces · $147 · final sale"), in the site's own type on paper, while the bag
     settles and Route adds its protection. No stamp, no badge (owner, Oct 21: nothing cheesy). It shows with reduced
     motion too, without the fade and with the hairline still (cns-cart.css).
     In the drawer it covers the panel as she sees it (fixed to the viewport, the panel's width), so it is on screen
     even when a short phone has scrolled the drawer; it sits beside .drawer__inner and survives repaints. */
  let goingT = 0;
  const dropGoing = () => {
    clearTimeout(goingT);
    document.querySelectorAll('.cns-going').forEach(x => x.remove());
    document.querySelectorAll('.cns-checkout[aria-busy]').forEach(x => x.removeAttribute('aria-busy'));
  };
  function showGoing(foot, note){
    const drawer = foot && foot.closest('cart-drawer'), inner = drawer && drawer.querySelector('.drawer__inner');
    const host = inner ? inner.parentElement : document.body;
    if (host.querySelector(':scope > .cns-going')) return;
    const n = Number(foot && foot.dataset.count) || 0, total = (foot && foot.dataset.total) || '';
    const fs = foot && foot.dataset.final === '1';
    const ov = document.createElement('div');
    ov.className = 'cns-going ' + (inner ? 'cns-going--drawer' : 'cns-going--page');
    if (inner){ const r = inner.getBoundingClientRect(); ov.style.left = Math.max(0, r.left) + 'px'; ov.style.width = r.width + 'px'; }
    ov.setAttribute('role', 'status');
    ov.innerHTML = '<p class="cns-going__t">off to checkout</p>'
      + `<p class="cns-d cns-mute cns-going__d">${n} ${n === 1 ? 'piece' : 'pieces'} · ${esc(total)}${fs ? ' · final sale' : ''}${note ? ' · gift note added' : ''}</p>`
      + '<span class="cns-going__line" aria-hidden="true"><i></i></span>';
    host.appendChild(ov);
    /* checkout never opened (offline, blocked): after 20s the bag is hers again */
    clearTimeout(goingT);
    goingT = setTimeout(() => { if (document.visibilityState === 'visible'){ S.checking = false; dropGoing(); } }, 20000);
  }
  /* the bag on screen against the server's: each bag's line keys and quantities (cns-cart-body data-keys: a swap on
     another page can keep the count and total and change the lines), its count and total (isStale), the header
     count and the gift note. On /cart the drawer stays shut (cart-drawer.js) and Dawn's changes there repaint only
     the page, so the shut drawer's copy may lag with nothing on screen stale: it is left out. */
  const keysOf = c => (c.items || []).map(i => i.key).join(',') + '|' + (c.items || []).map(i => i.quantity).join(',');
  function changed(c){
    const n = document.querySelector('#cart-icon-bubble .cns-bag__n'), ta = document.querySelector('[data-cns-note]');
    const onCart = !!document.querySelector('cart-items.cns-cart-page');
    const shown = el => !(onCart && el.closest('cart-drawer:not(.active)'));
    const bs = bags().filter(shown);
    return isStale(c, [...bs, ...feet().filter(shown)]) || bs.some(b => b.dataset.keys !== keysOf(c))
      || (!!n && n.textContent.trim() !== String(c.item_count))
      || (!!ta && ta.value !== String(c.note || '').replace(/\r\n?/g, '\n'));
  }
  /* back from checkout or another page (a page restored from the back-forward cache): the bag is usable again, and
     everything it shows is the bag as she left it, the drawer still open if it was. One read of the bag: when it
     changed (or could not be read), all of it repaints from the server, so a row that has gone is off screen before
     she taps it; when it did not, nothing is touched (the drawer keeps its scroll, focus stays, Route does not mount
     again). A tap made meanwhile waits in the queue behind this; a quantity tap is judged by the row it was made on
     (staleQty; by the bag read here when its repaint failed), the suggestion's add by the card it was made on
     (addUpsell). Only then: the first pageshow of a fresh page fires on load, which would clear a tap that is still
     waiting for Route. */
  addEventListener('pageshow', e => {
    if (!e.persisted) return;
    S.passing = false; S.checking = false; S.viaRoute = false;
    dropGoing();
    gen++;
    document.querySelectorAll('input[data-quantity-line-key], [data-cns-upsell]').forEach(i => { i.__cnsOld = true; });
    S.back++;
    enqueue(async () => {
      try {
        const c = await getCart().catch(() => null);
        S.backCart = null;
        if ((!c || changed(c)) && !(await paint())) S.backCart = c;
      } finally { S.back--; }
    });
  });

  /* ---------- checkout: let the bag settle, save the attributes, say where she is going, go ---------- */
  function checkout(form, btn){
    S.checking = true;
    const foot = btn.closest('[data-cns-foot]');
    const ctx = foot ? foot.dataset.cnsFoot : '';
    const bag = form.querySelector('[data-cns-bag]') || document.querySelector(`[data-cns-bag="${ctx}"]`);
    const attrs = {};
    if (bag) attrs.boxes = bag.dataset.mixed ? ((bag.querySelector('[data-cns-boxes]:checked') || {}).value || 'one') : '';
    const ta = bag && bag.querySelector('[data-cns-note]');
    const body = {attributes:attrs};
    if (ta) body.note = ta.value.slice(0, NOTE_MAX);
    const formId = form.id, btnId = btn.id;
    /* Route hides our button behind its own copy (data-route-ref) and taps ours itself once protection is added;
       a tap on ours while it has no copy came straight from her, before Route mounted again after a repaint */
    const direct = !btn.hasAttribute('data-route-ref');
    showGoing(foot, ta && ta.value.trim());
    /* the queue (quantity changes, a gift being added) must finish first, so the form posts the bag as it is. A gift
       add that failed a moment ago, or a gift line still on its way out (cns-line--leaving), gets one more sync. */
    const resync = retrying() || !!document.querySelector('.cns-line--leaving');
    const busy = resync || pending > 0 || Date.now() - S.lastQty < 400;
    const ready = (async () => {
      const saved = post(routesOf().cart_update_url, body, true).catch(() => null);
      const settle = S.lastQty + 360 - Date.now();      /* Dawn waits 300ms after a quantity tap */
      if (settle > 0) await wait(settle);
      await Promise.all([saved, ...S.saves]);
      await idle();
      if (resync){ [1, 2].forEach(n => { if (isFailed(n) && !charged(n)) delete S.failed[n]; }); await enqueue(() => syncNow(true)); }
    })();
    Promise.race([ready, wait(busy ? 3000 : 900)]).then(async () => {
      const f = document.getElementById(formId) || form;
      const b = (btnId && document.getElementById(btnId)) || btn;
      /* give Route's button the tap (when it comes in time), so it adds the protection first */
      if (direct && !S.viaRoute){
        const live = (foot && document.getElementById(foot.id)) || foot;
        b.setAttribute('aria-busy', 'true');
        const r = await routeHandoff(live);
        b.removeAttribute('aria-busy');
        if (r){ S.viaRoute = true; S.checking = false; r.click(); setTimeout(() => { S.viaRoute = false; }, 8000); return; }
      }
      S.passing = true;
      if (f.requestSubmit && b.isConnected && b.form === f) f.requestSubmit(b);
      else { const h = document.createElement('input'); h.type = 'hidden'; h.name = 'checkout'; h.value = '1'; f.appendChild(h); f.submit(); }
      setTimeout(() => { S.passing = false; S.checking = false; }, 5000);
    });
  }

  document.addEventListener('submit', e => {
    const form = e.target;
    if (!form || !(form.id === 'CartDrawer-Form' || form.id === 'cart')) return;
    if (S.passing) return;
    const btn = e.submitter;
    if (isRoute(btn)) return;
    if (!btn || btn.name !== 'checkout') return;
    /* Enter in a quantity box should update the line, not check out */
    const a = document.activeElement;
    if (a && /^updates\[/.test(a.name || '')){ e.preventDefault(); a.blur(); return; }
    e.preventDefault();
    if (S.checking) return;
    checkout(form, btn);
  }, true);

  /* a tap on Route's copy of checkout (Route adds its protection first, then taps ours, which runs checkout above):
     say "off to checkout" right away, so the tap shows it was taken while Route works */
  document.addEventListener('click', e => {
    const b = e.target instanceof Element && e.target.closest('button[name^="button-route"]');
    const foot = b && b.closest('[data-cns-foot]');
    if (!foot || S.passing) return;
    const ta = document.querySelector(`[data-cns-bag="${foot.dataset.cnsFoot}"] [data-cns-note]`);
    showGoing(foot, ta && ta.value.trim());
  }, true);

  /* Enter in a bag field never submits the cart form (that would be checkout); in a quantity box it commits the number */
  document.addEventListener('keydown', e => {
    if (e.key !== 'Enter' || e.isComposing) return;
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || !t.form || !(t.form.id === 'CartDrawer-Form' || t.form.id === 'cart')) return;
    e.preventDefault();
    if (/^updates\[/.test(t.name || '')){ t.blur(); t.focus(); }
  }, true);

  /* the bag's own controls; their change events never reach Dawn's quantity handler */
  document.addEventListener('change', e => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.matches('[data-cns-boxes]')){ e.stopPropagation(); onBoxes(t); }
    else if (t.matches('[data-cns-note]')){ e.stopPropagation(); onNoteSave(t); }
    else if (t.matches('[data-cns-gift-size]')){ e.stopPropagation(); onGiftSize(t); }
    else if (t.matches('[data-quantity-line-key]')) S.lastQty = Date.now();
  }, true);
  document.addEventListener('input', e => {
    const t = e.target;
    if (t instanceof Element && t.matches('[data-cns-note]')) onNoteInput(t);
  }, true);
  document.addEventListener('click', e => {
    const t = e.target instanceof Element && e.target.closest('[data-cns-note-toggle],[data-cns-upsell],[data-cns-size-jump],[data-cns-jump],[data-cns-jump-drawer]');
    if (!t) return;
    if (t.hasAttribute('data-cns-note-toggle')) return toggleNote(t);
    e.preventDefault();
    if (t.hasAttribute('data-cns-upsell')) return addUpsell(t);
    const behavior = reduce() ? 'auto' : 'smooth';
    if (t.hasAttribute('data-cns-size-jump')){
      const row = document.getElementById((t.getAttribute('href') || '').slice(1));
      if (!row) return;
      row.scrollIntoView({block:'center', behavior});
      const r = row.querySelector('[data-cns-gift-size]:checked') || row.querySelector('[data-cns-gift-size]:not(:disabled)');
      if (r) r.focus({preventScroll:true});
      return;
    }
    if (t.hasAttribute('data-cns-jump-drawer')){
      const foot = document.getElementById('CnsFoot-drawer');
      if (!foot) return;
      foot.scrollIntoView({block:'end', behavior});
      const go = foot.querySelector('[data-cns-checkout]');
      if (go) go.focus({preventScroll:true});
      return;
    }
    const foot = document.getElementById('CnsFoot-page');
    if (!foot) return;
    foot.scrollIntoView({block:'start', behavior});
    const go = foot.querySelector('[data-cns-checkout]');
    if (go) go.focus({preventScroll:true});
  });

  /* ---------- /cart on a phone: the checkout bar while the footer is off screen ---------- */
  let jumpIO = null, jumpFoot = null, jumpWant = false;
  /* the bar and the loyalty launcher never share the corner: html.cns-jump-on hides the launcher on a phone */
  function jumpShow(on){
    jumpWant = on;
    const j = document.querySelector('[data-cns-jumpbar]');
    if (j && j.hidden === on) j.hidden = !on;
    document.documentElement.classList.toggle('cns-jump-on', !!(j && on));
  }
  function watchJump(){
    const foot = document.getElementById('CnsFoot-page');
    if (!foot){ if (document.documentElement.classList.contains('cns-jump-on')) jumpShow(false); return; }
    if (!('IntersectionObserver' in window)) return;
    if (!jumpIO) jumpIO = new IntersectionObserver(es => es.forEach(en => jumpShow(!en.isIntersecting && en.boundingClientRect.top > 0)));
    if (foot !== jumpFoot){ jumpIO.disconnect(); jumpIO.observe(foot); jumpFoot = foot; jumpShow(jumpWant); }
  }

  /* ---------- the drawer 650px tall or less: the same bar while the footer's checkout is below the fold ---------- */
  let djIO = null, djRoot = null, djFoot = null;
  function watchDrawerJump(){
    const inner = document.querySelector('cart-drawer .drawer__inner');
    const bar = inner && inner.querySelector('[data-cns-jumpbar-drawer]'), foot = inner && inner.querySelector('#CnsFoot-drawer');
    if (!bar || !foot || !('IntersectionObserver' in window)){ if (djIO){ djIO.disconnect(); djFoot = null; } return; }
    const a = bar.querySelector('[data-cns-jump-drawer]');
    if (a && foot.dataset.total){ setText(a.querySelector('[data-cns-jump-total]'), foot.dataset.total); a.setAttribute('aria-label', 'go to checkout · ' + foot.dataset.total); }
    if (inner !== djRoot){
      if (djIO) djIO.disconnect();
      djRoot = inner; djFoot = null;
      /* the footer's lower edge (checkout; Route hides our button behind its copy, so not the button itself) below
         the panel's visible part: the bar shows (it only ever shows under 650px tall, cns-cart.css) */
      djIO = new IntersectionObserver(es => es.forEach(en => {
        const b = djRoot.querySelector('[data-cns-jumpbar-drawer]'), r = en.rootBounds;
        const on = !!r && en.boundingClientRect.bottom > r.bottom + 1;
        if (b && b.hidden === on) b.hidden = !on;
      }), {root:inner, threshold:[0, 0.5, 1]});
    }
    if (foot !== djFoot){ djIO.disconnect(); djIO.observe(foot); djFoot = foot; }
  }

  /* ---------- watch the bag ---------- */
  let queued = false;
  function onRender(){
    if (queued) return; queued = true;
    Promise.resolve().then(() => { queued = false; hydrate(); if (!syncFx() && needsGiftSync()) syncGifts(); });
  }
  const mo = new MutationObserver(onRender);
  let drawerWatch = null;
  function watch(){
    mo.disconnect();
    hosts().forEach(h => mo.observe(h, {childList:true, subtree:true}));
    const drawer = document.querySelector('cart-drawer');
    if (drawerWatch) drawerWatch.disconnect();
    if (drawer){
      let was = drawer.classList.contains('active');
      /* html.cns-bag-open while the drawer is open: the loyalty launcher steps aside (cns-cart.css) */
      document.documentElement.classList.toggle('cns-bag-open', was);
      drawerWatch = new MutationObserver(() => {
        const is = drawer.classList.contains('active');
        if (is !== was) document.documentElement.classList.toggle('cns-bag-open', is);
        if (is && !was){
          syncGifts();
          fillUps(true);
          /* Dawn moves focus in on transitionend, which never fires with reduced motion (no transitions):
             move it in ourselves when it has not arrived */
          setTimeout(() => {
            if (drawer.classList.contains('active') && !drawer.contains(document.activeElement)) trapIn(drawer, null);
          }, reduce() ? 40 : 600);
        }
        was = is;
      });
      drawerWatch.observe(drawer, {attributes:true, attributeFilter:['class']});
    }
  }
  /* gift thresholds are set in the shop currency; the discounts convert them at Shopify's rate. Hand that rate to
     Liquid as the hidden cart attribute _fx so a local-currency bag unlocks at the amount checkout uses.
     Runs after every render, so a first visit's first add (the bag was empty at load) gets it too. True when it
     queued the write: the repaint after it syncs the gifts, with the converted thresholds. */
  function syncFx(){
    const cur = window.Shopify && window.Shopify.currency, bag = bags()[0];
    if (!bag) S.fxSent = null;
    if (!cur || !bag || !(Number(bag.dataset.count) > 0)) return false;
    /* a page back from the back/forward cache after she switched country elsewhere: its rate is the old currency's
       (the page she switched on wrote the right one) */
    if (cur.active && bag.dataset.ccy && cur.active !== bag.dataset.ccy) return false;
    const local = bag.dataset.ccy && bag.dataset.shopCcy && bag.dataset.ccy !== bag.dataset.shopCcy;
    const want = local && Number(cur.rate) > 0 ? String(Number(cur.rate)) : '';
    if ((bag.dataset.fx || '') === want || S.fxSent === want) return false;
    S.fxSent = want; S.fxAt = Date.now();
    enqueue(() => post(routesOf().cart_update_url, {attributes:{_fx: want}}).catch(() => null)).then(() => refresh());
    return true;
  }
  /* the bag on screen still has the unconverted thresholds while its _fx write is on its way: no gift sync from it
     (a gift added at the wrong threshold comes back charged and rests for 10 minutes). Lets go after 8s. */
  const fxPending = () => { const bag = bags()[0]; return !!bag && S.fxSent !== null && (bag.dataset.fx || '') !== S.fxSent && Date.now() - S.fxAt < 8000; };
  function init(){ hookDawn(); listenDawn(); watch(); hydrate(); if (!syncFx() && needsGiftSync()) syncGifts(); warmSoon(); }
  CNSCart.init = init;
  init();
  document.addEventListener('shopify:section:load', init);
})();
